import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link2, Copy, Check } from 'lucide-react';
import { liveScoreAPI } from '../services/api';

function LiveMatchCard({ match }) {
  const team1Score = match.team1?.score;
  const team2Score = match.team2?.score;

  return (
    <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-500">
      <div className="flex justify-between items-start mb-4">
        <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded animate-pulse">
          LIVE
        </span>
        <span className="text-sm text-gray-500">{match.format}</span>
      </div>

      <div className="space-y-4">
        {/* Team 1 */}
        <div className={`flex justify-between items-center ${team1Score?.is_batting ? 'font-bold' : ''}`}>
          <span className="text-lg">{match.team1.name}</span>
          {team1Score && (
            <span className="text-xl">
              {team1Score.runs}/{team1Score.wickets}
              <span className="text-sm text-gray-500 ml-1">({team1Score.overs})</span>
            </span>
          )}
        </div>

        {/* Team 2 */}
        <div className={`flex justify-between items-center ${team2Score?.is_batting ? 'font-bold' : ''}`}>
          <span className="text-lg">{match.team2.name}</span>
          {team2Score && (
            <span className="text-xl">
              {team2Score.runs}/{team2Score.wickets}
              <span className="text-sm text-gray-500 ml-1">({team2Score.overs})</span>
            </span>
          )}
        </div>
      </div>

      {/* Current Batsmen */}
      {match.current_batsmen && (
        <div className="mt-4 pt-4 border-t">
          <p className="text-sm text-gray-500 mb-2">Batting:</p>
          <div className="space-y-1">
            {match.current_batsmen.map((batsman, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span>
                  {batsman.player_name}
                  {batsman.is_on_strike && ' *'}
                </span>
                <span>{batsman.runs} ({batsman.balls})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Current Bowler */}
      {match.current_bowler && (
        <div className="mt-2">
          <p className="text-sm text-gray-500">Bowling: {match.current_bowler.player_name} {match.current_bowler.wickets}/{match.current_bowler.runs} ({match.current_bowler.overs})</p>
        </div>
      )}

      {/* Chase Info */}
      {match.chase_info && (
        <div className="mt-4 pt-4 border-t bg-blue-50 -mx-6 -mb-6 px-6 py-3 rounded-b-lg">
          <p className="text-center font-medium text-blue-800">
            Need {match.chase_info.runs_needed} runs from {match.chase_info.balls_remaining} balls
          </p>
          <p className="text-center text-sm text-blue-600">
            RRR: {match.chase_info.required_run_rate?.toFixed(2)}
          </p>
        </div>
      )}

      {/* Current Over */}
      {match.current_over && match.current_over.length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <p className="text-sm text-gray-500 mb-2">This over:</p>
          <div className="flex space-x-2">
            {match.current_over.map((ball, idx) => (
              <span
                key={idx}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                  ${ball.is_wicket ? 'bg-red-500 text-white' :
                    ball.is_boundary ? 'bg-green-500 text-white' :
                    ball.is_wide || ball.is_no_ball ? 'bg-yellow-400' :
                    'bg-gray-200'}`}
              >
                {ball.is_wicket ? 'W' :
                 ball.is_wide ? 'Wd' :
                 ball.is_no_ball ? 'Nb' :
                 ball.runs_scored}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 pt-4 border-t text-sm text-gray-500">
        {match.venue}
      </div>
    </div>
  );
}

function UpcomingMatchCard({ match }) {
  // Append 'Z' to indicate UTC so browser converts to local timezone
  const matchDate = match.match_date ? new Date(match.match_date.endsWith('Z') ? match.match_date : match.match_date + 'Z') : null;
  const isValidDate = matchDate && !isNaN(matchDate.getTime());

  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <div className="flex justify-between items-start mb-2">
        <span className="text-xs text-gray-500">{match.tournament?.name}</span>
        <span className="text-xs text-gray-500">{match.format}</span>
      </div>

      <div className="flex justify-between items-center py-2">
        <span className="font-medium">{match.team1.name}</span>
        <span className="text-gray-400">vs</span>
        <span className="font-medium">{match.team2.name}</span>
      </div>

      <div className="text-sm text-gray-500 text-center">
        {isValidDate
          ? `${matchDate.toLocaleDateString()} at ${matchDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
          : 'Date TBD'}
      </div>
      <div className="text-xs text-gray-400 text-center mt-1">{match.venue}</div>
    </div>
  );
}

function RecentMatchCard({ match }) {
  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <div className="flex justify-between items-start mb-2">
        <span className="text-xs text-gray-500">{match.tournament?.name}</span>
        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">Completed</span>
      </div>

      <div className="space-y-2 py-2">
        <div className={`flex justify-between ${match.winner === match.team1.id ? 'font-bold' : ''}`}>
          <span>{match.team1.name}</span>
          {match.team1.score && (
            <span>{match.team1.score.runs}/{match.team1.score.wickets}</span>
          )}
        </div>
        <div className={`flex justify-between ${match.winner === match.team2.id ? 'font-bold' : ''}`}>
          <span>{match.team2.name}</span>
          {match.team2.score && (
            <span>{match.team2.score.runs}/{match.team2.score.wickets}</span>
          )}
        </div>
      </div>

      {match.result && (
        <div className="text-sm text-green-600 font-medium text-center pt-2 border-t">
          {match.result}
        </div>
      )}

      {match.man_of_the_match && (
        <div className="text-xs text-gray-500 text-center mt-1">
          MoM: {match.man_of_the_match.name}
        </div>
      )}
    </div>
  );
}

function ShareableLink() {
  const [copied, setCopied] = useState(false);
  const liveUrl = `${window.location.origin}/public/live`;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(liveUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = liveUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Link2 className="h-5 w-5 text-blue-600 flex-shrink-0" />
          <span className="text-sm font-medium text-blue-800">Public Live Score URL:</span>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
          <code className="bg-white px-3 py-1.5 rounded border border-blue-200 text-xs sm:text-sm text-blue-700 truncate overflow-hidden">
            {liveUrl}
          </code>
          <button
            onClick={copyToClipboard}
            className={`flex items-center justify-center gap-1 px-3 py-1.5 rounded text-sm font-medium transition-colors flex-shrink-0 ${
              copied
                ? 'bg-green-500 text-white'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {copied ? (
              <>
                <Check className="h-4 w-4" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" />
                Copy
              </>
            )}
          </button>
        </div>
      </div>
      <p className="text-xs text-blue-600 mt-2">
        Share this link with anyone to let them view live scores (no login required)
      </p>
    </div>
  );
}

export default function LiveScore() {
  const [activeTab, setActiveTab] = useState('live');

  const { data: liveMatches = [], refetch: refetchLive } = useQuery({
    queryKey: ['liveMatches'],
    queryFn: liveScoreAPI.getLiveMatches,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const { data: todaysMatches = [] } = useQuery({
    queryKey: ['todaysMatches'],
    queryFn: liveScoreAPI.getTodaysMatches,
  });

  const { data: upcomingMatches = [] } = useQuery({
    queryKey: ['upcomingMatches'],
    queryFn: () => liveScoreAPI.getUpcomingMatches(7),
  });

  const { data: recentMatches = [] } = useQuery({
    queryKey: ['recentMatches'],
    queryFn: () => liveScoreAPI.getRecentMatches(7),
  });

  // Auto-refresh live matches
  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTab === 'live') {
        refetchLive();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [activeTab, refetchLive]);

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Live Scores</h1>

      {/* Shareable Link */}
      <ShareableLink />

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 mb-6 bg-gray-100 p-1 rounded-lg w-full sm:w-fit">
        <button
          onClick={() => setActiveTab('live')}
          className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-md text-sm ${activeTab === 'live' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
        >
          Live ({liveMatches.length})
        </button>
        <button
          onClick={() => setActiveTab('today')}
          className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-md text-sm ${activeTab === 'today' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
        >
          Today ({todaysMatches.length})
        </button>
        <button
          onClick={() => setActiveTab('upcoming')}
          className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-md text-sm ${activeTab === 'upcoming' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
        >
          Upcoming
        </button>
        <button
          onClick={() => setActiveTab('recent')}
          className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-md text-sm ${activeTab === 'recent' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
        >
          Recent
        </button>
      </div>

      {/* Live Matches */}
      {activeTab === 'live' && (
        <div>
          {liveMatches.length === 0 ? (
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <p className="text-gray-500">No live matches at the moment</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {liveMatches.map((match) => (
                <LiveMatchCard key={match.match_id} match={match} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Today's Matches */}
      {activeTab === 'today' && (
        <div>
          {todaysMatches.length === 0 ? (
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <p className="text-gray-500">No matches scheduled for today</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {todaysMatches.map((match) => (
                <UpcomingMatchCard key={match.match_id} match={match} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Upcoming Matches */}
      {activeTab === 'upcoming' && (
        <div>
          {upcomingMatches.length === 0 ? (
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <p className="text-gray-500">No upcoming matches</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {upcomingMatches.map((match) => (
                <UpcomingMatchCard key={match.match_id} match={match} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Recent Matches */}
      {activeTab === 'recent' && (
        <div>
          {recentMatches.length === 0 ? (
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <p className="text-gray-500">No recent matches</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentMatches.map((match) => (
                <RecentMatchCard key={match.match_id} match={match} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
